import { createSlice } from "@reduxjs/toolkit";
import { AppThunk } from "../store/configureStore";
import ZonesService from "Services/zones";
import { ICreateZoneProps, IZone, IZoneSettings } from "../types/zone";
import SitesService, { PLACEMENT_TYPE } from "../Services/sites";
import AnalyticsService, { EVENT } from "../Services/analytics";
import { toast } from "react-toastify";
import I18nService from "../Services/i18n";

interface IZoneState {
  loading: boolean;
  data: IZone[];
  tagSettings: any;
  zoneData: any;
  errors: any;
}

const initialState: IZoneState = {
  loading: false,
  data: [],
  tagSettings: {},
  zoneData: {},
  errors: {},
};

const zoneSlice = createSlice({
  name: "zones",
  initialState,
  reducers: {
    setLoader: (state) => {
      state.loading = true;
      state.errors = initialState.errors;
    },
    setData: (state, action) => {
      const { payload } = action;
      state.loading = false;
      state.data = payload;
    },
    setTagSettings: (state, action) => {
      const { payload } = action;
      state.loading = false;
      state.tagSettings = payload;
    },
    setError: (state, action) => {
      const { payload } = action;
      state.loading = false;
      state.errors = payload;
    },
    finishRequest: (state) => {
      state.loading = false;
    },
    setZoneData: (state, action) => {
      const { payload } = action;
      state.zoneData = payload;
    },
  },
});

export const {
  setLoader,
  setData,
  setTagSettings,
  setError,
  finishRequest,
  setZoneData,
} = zoneSlice.actions;

export const getZones = (directions?: [], sitesId?: []): AppThunk => async (
  dispatch
) => {
  dispatch(setLoader());
  try {
    const response = await ZonesService.getAllZones(directions, sitesId);
    response.sort(
      (zoneA: any, zoneB: any) => zoneB?.createdAt - zoneA?.createdAt
    );
    dispatch(setData(response));
  } catch (error) {
    dispatch(setError(error));
  }
};

export const getTagSettings = (zoneId: number): AppThunk => async (
  dispatch
) => {
  dispatch(setLoader());
  try {
    const response = await ZonesService.getNotixTagSettings(zoneId);
    dispatch(setTagSettings(response));
  } catch (error) {
    dispatch(setError(error));
  }
};

export const editNotixZoneSettings = (
  id: number,
  zoneSettings: IZoneSettings
): AppThunk => async (dispatch) => {
  dispatch(setLoader());
  try {
    await ZonesService.editNotixZoneSettings(id, zoneSettings);
    dispatch(finishRequest());
  } catch (error) {
    dispatch(setError(error));
  }
};

export const createZone = (
  createZoneProps: ICreateZoneProps
): AppThunk => async (dispatch) => {
  dispatch(setLoader());
  try {
    setZoneData({});
    const { source, zoneTagName, typeTag, siteId } = createZoneProps;

    let siteID = siteId;

    if (!siteId) {
      const placementData = {
        url: source,
        type: PLACEMENT_TYPE.WEBSITE,
      };

      const site = await SitesService.addSite(placementData);
      AnalyticsService.trackEvent(
        EVENT.NAME.UA_EVENT,
        EVENT.CATEGORY.SITES,
        EVENT.ACTION.PUB_ADD_SITE_SUCCESS,
        site.id
      );

      siteID = site.id;
    }

    const zone = await ZonesService.createNotixZone(siteID, zoneTagName, {
      app_type: typeTag,
    });
    AnalyticsService.trackEvent(
      EVENT.NAME.UA_EVENT,
      EVENT.CATEGORY.ZONES,
      EVENT.ACTION.PUB_ZONE_ADDED,
      zone.id
    );
    toast.success(I18nService.getStringByKey("toast_success-zone-create"));
    dispatch(setZoneData({ ...zone, siteId: siteID }));
    dispatch(finishRequest());
  } catch (error) {
    toast.error(error.url?.join());
    dispatch(finishRequest());
    dispatch(setError(error));
  }
};

export default zoneSlice.reducer;
